<script setup lang="ts">
  import type { Dispute, DisputeContextData, Task, TaskType } from '@api/models';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted, useTemplateRef } from 'vue';

  import { Card, CardContent } from '@ui/components/card';

  import DisputeForm from '@/components/disputes/DisputeForm.vue';
  import { TaskTypeMap } from '@/constants/TaskConstants';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    disabled?: boolean;
    task?: Task;
  }

  const props = defineProps<Props>();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);

  const DisputeFormRef = useTemplateRef<InstanceType<typeof DisputeForm>>('disputeFormRef');

  const componentTask = computed(() => props.task ?? currentTask.value);
  const dispute = computed((): Dispute | null | undefined => componentTask.value?.dispute);

  const disputeTaskData = computed(
    (): DisputeContextData | null | undefined => componentTask.value?.disputeTaskData,
  );

  const initialDisputeData = computed(() => {
    return {
      amount: dispute.value?.itemAmount || 0,
      assignee: dispute.value?.assignee_id,
      currency: dispute.value?.currency || 'USD',
      description: dispute.value?.description,
      invoice: dispute.value?.disputeItem?.invoice_id,
      partner: dispute.value?.partner_id,
      priority: dispute.value?.priority,
      reason: dispute.value?.reason_id,
    };
  });

  const initialTaskDisputeData = computed(() => {
    return {
      amount: disputeTaskData.value?.disputed_amount || 0,
      assignee: disputeTaskData.value?.assignee_id,
      currency: disputeTaskData.value?.currency || 'USD',
      description: disputeTaskData.value?.description,
      invoice: disputeTaskData.value?.invoice_id,
      partner: componentTask.value?.partner_id,
      priority: disputeTaskData.value?.priority,
      reason: disputeTaskData.value?.matched_option_id,
    };
  });

  const initialData = computed(() => {
    if (componentTask.value?.isDisputeCreate) {
      return initialTaskDisputeData.value;
    }

    return initialDisputeData.value;
  });

  const updateData = computed(() => {
    if (componentTask.value?.isDisputeUpdate) {
      return initialTaskDisputeData.value;
    }

    return undefined;
  });

  onMounted(() => {
    if (componentTask.value?.isDisputeCreate) {
      DisputeFormRef?.value?.validateFormData?.();
    }
  });

  defineExpose({
    DisputeFormRef,
    formData: computed(() => DisputeFormRef?.value?.formData),
    getRequestParams: DisputeFormRef?.value?.getRequestParams,
    initialData,
    submit: DisputeFormRef?.value?.submit,
    submitting: computed(() => DisputeFormRef?.value?.submitting),
    updateData,
    validateForm: DisputeFormRef?.value?.validateFormData,
  });
</script>

<template>
  <div
    class="space-y-4"
    :class="{
      'opacity-70': !componentTask?.isOpen,
    }"
  >
    <div
      v-if="TaskTypeMap[componentTask?.type as TaskType]?.subtitle"
      class="flex items-center gap-x-2"
    >
      <i class="fas fa-star-christmas text-primary text-xs"></i>

      <strong>
        {{ TaskTypeMap[componentTask?.type as TaskType]?.subtitle }}
      </strong>
    </div>

    <Card>
      <CardContent>
        <DisputeForm
          ref="disputeFormRef"
          :key="componentTask?.id"
          :disabled="disabled"
          :initial-data="initialData"
          :update-data="updateData"
        />
      </CardContent>
    </Card>
  </div>
</template>

<style scoped></style>
